import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import type { TFunction } from 'i18next';
import { getApiErrorMessage } from '@/api/client';
import { useMyAccount, useMyCategory, useMyClaims, useMyLedger, useMyPillarThreeProjection, useMyProfile, useSelectYearlyRelease } from '@/hooks/useMobility';
import { formatCurrency } from '@/utils/currency';
import { formatBelgianDate } from '@/utils/date';
import { getBudgetPeriodForYear, getMobilityYearOptions } from '@/utils/mobilityYears';
import type { ExpenseClaim, MobilityLedgerEntry } from '@/types/mobility';

// EarlyRelease/EarlyReleaseReversal are administrator-granted advances against
// the annual entitlement (see MobilityLedgerEntryType on the backend) and their
// reversals. The early-release advance mechanism is being superseded by the
// yearly (lump-sum) release option and is no longer meant to be shown to
// employees, but it hasn't been fully removed from the backend yet, so these
// entry types are filtered out of the employee-facing ledger view in the
// meantime. Revisit once early release is fully decommissioned.
const HIDDEN_LEDGER_ENTRY_TYPES = new Set(['EarlyRelease', 'EarlyReleaseReversal']);

/**
 * The employee's mobility budget overview page: current balance, category
 * and vehicle profile, Pillar 3 cash-payout projection, and the year's
 * ledger transactions.
 *
 * Shows an informational message instead of the dashboard when the signed-in
 * account isn't linked to a mobility profile, and hides budget-specific
 * sections when mobility budget is linked but not enabled for the employee.
 *
 * @returns The budget overview page.
 */
export function MobilityMyBudgetPage() {
  const { t, i18n } = useTranslation();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [switchingRelease, setSwitchingRelease] = useState(false);
  const currentYear = new Date().getFullYear();

  const { data: profile, isLoading: profileLoading } = useMyProfile();
  const linked = profile?.isLinked ?? false;
  const budgetEnabled = profile?.mobilityBudgetEnabled ?? false;
  const { data: account, isError: accountError } = useMyAccount(year, linked && budgetEnabled);
  const { data: category } = useMyCategory(linked);
  const { data: ledger, isLoading: ledgerLoading } = useMyLedger(year, linked && budgetEnabled);
  const { data: claims } = useMyClaims(year, linked && budgetEnabled);
  const { data: pillarThree } = useMyPillarThreeProjection(year, linked && budgetEnabled);
  const yearOptions = useMemo(
    () => getMobilityYearOptions(category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate),
    [category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate],
  );
  const budgetPeriod = useMemo(
    () => getBudgetPeriodForYear(year, category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate),
    [year, category?.vehicleLeaseStartDate, category?.vehicleLeaseEndDate],
  );
  const claimsById = useMemo(
    () => new Map((claims ?? []).map((claim) => [claim.id, claim])),
    [claims],
  );

  const visibleLedger = useMemo(
    () => (ledger ?? []).filter((entry) => !HIDDEN_LEDGER_ENTRY_TYPES.has(entry.entryType)),
    [ledger],
  );

  const expenses = (ledger ?? [])
    .filter((entry) => entry.entryType === 'ExpenseReimbursement')
    .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

  const columns: GridColDef<MobilityLedgerEntry>[] = useMemo(
    () => [
      {
        field: 'occurredOn',
        headerName: t('mobility.fields.date'),
        width: 125,
        valueFormatter: (value) => formatBelgianDate(value as string),
      },
      {
        field: 'entryType',
        headerName: t('mobility.fields.entryType'),
        width: 190,
        valueFormatter: (value) => t(`mobility.ledger.types.${String(value)}`),
      },
      {
        field: 'description',
        headerName: t('mobility.fields.description'),
        flex: 1,
        minWidth: 260,
        valueGetter: (_value, row) => ledgerDescription(row, claimsById, t),
      },
      {
        field: 'amount',
        headerName: t('mobility.fields.amount'),
        width: 165,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ value, row }) => {
          const amount = Number(value ?? 0);
          return (
            <Typography fontWeight={700} color={amount < 0 ? 'error.main' : 'success.dark'}>
              {amount > 0 ? '+' : ''}{formatCurrency(amount, row.currency, i18n.language)}
            </Typography>
          );
        },
      },
    ],
    [t, i18n.language, claimsById],
  );

  if (!profileLoading && !linked) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography fontWeight={600}>{t('mobility.self.notLinkedTitle')}</Typography>
        {t('mobility.self.notLinked')}
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={750}>{t('mobility.self.budgetTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('mobility.self.budgetSubtitle')}</Typography>
        </Box>
        {budgetEnabled && <TextField
          select
          size="small"
          label={t('mobility.fields.year')}
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
          sx={{ minWidth: 120 }}
        >
          {yearOptions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </TextField>}
      </Stack>

      {!profileLoading && linked && !budgetEnabled && (
        <Alert severity="info">
          <Typography fontWeight={700}>{t('mobility.self.budgetInactiveTitle')}</Typography>
          {t('mobility.self.budgetInactiveMessage')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="stretch">
        {budgetEnabled && <Card variant="outlined" sx={cardSx}>
          <CardContent sx={cardContentSx}>
            <CardHeading icon={<AccountBalanceWalletOutlinedIcon />} title={t('mobility.self.balanceCardTitle')} subtitle={t('mobility.self.balanceCardSubtitle')} primary />
            {account ? (
              <>
                <Typography variant="h3" fontWeight={750} sx={{ letterSpacing: '-0.03em' }}>
                  {formatCurrency(account.availableBalance, account.currency, i18n.language)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('mobility.self.availableNow')}</Typography>
                <Divider sx={{ my: 2.5 }} />
                <Stack spacing={1.5}>
                  <BudgetGroup title={t('mobility.self.budgetComposition')}>
                    <BudgetLine label={t('mobility.self.annualBudgetPillars')} value={formatCurrency(account.annualBudget, account.currency, i18n.language)} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      {budgetPeriod
                        ? t('mobility.self.annualBudgetExplanation', {
                            year,
                            startDate: formatBelgianDate(budgetPeriod.activeFrom),
                            endDate: formatBelgianDate(budgetPeriod.activeUntil),
                          })
                        : t('mobility.self.annualBudgetExplanationNoLease', { year })}
                    </Typography>
                    {account.releaseMode === 'Yearly' && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {t('mobility.self.yearlyReleaseLeaverDisclaimer', { year })}
                      </Typography>
                    )}
                    {account.releaseMode !== 'Yearly' && (
                      <BudgetLine label={t('mobility.self.regularReleased')} value={formatCurrency(account.standardReleasedBudget, account.currency, i18n.language)} />
                    )}
                    {account.releaseMode === 'Yearly' ? (
                      <Chip size="small" color="primary" variant="outlined" label={t('mobility.self.releaseModeYearly')} sx={{ alignSelf: 'flex-start', mt: 0.5 }} />
                    ) : year === currentYear && !account.isClosed ? (
                      <Button size="small" onClick={() => setSwitchingRelease(true)} sx={{ alignSelf: 'flex-start', mt: 0.5, px: 0 }}>
                        {t('mobility.self.switchToYearlyRelease')}
                      </Button>
                    ) : null}
                  </BudgetGroup>
                  <BudgetGroup title={t('mobility.self.usageAndProjection')}>
                    <BudgetLine label={t('mobility.self.expenses')} value={formatCurrency(expenses, account.currency, i18n.language)} />
                    {pillarThree && <BudgetLine
                      label={t('mobility.self.projectedPillarThree')}
                      value={formatCurrency(pillarThree.netAmount, pillarThree.currency, i18n.language)}
                      accent
                    />}
                  </BudgetGroup>
                </Stack>
                {pillarThree && <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                    {t('mobility.self.pillarThreeDescription')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                    {t('mobility.self.pillarThreeExplanation', {
                      gross: formatCurrency(pillarThree.grossAmount, pillarThree.currency, i18n.language),
                      contribution: formatCurrency(pillarThree.contributionAmount, pillarThree.currency, i18n.language),
                      rate: (pillarThree.contributionRate * 100).toLocaleString(i18n.language, { maximumFractionDigits: 2 }),
                    })}
                  </Typography>
                </Box>}
                <Alert severity="info" icon={<CalendarMonthOutlinedIcon />} sx={{ mt: 2.5 }}>{t('mobility.self.monthEndReleaseInfo')}</Alert>
                {account.isClosed && <Chip size="small" label={t('mobility.accounts.stateClosed')} sx={{ mt: 1.5 }} />}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {accountError ? t('mobility.self.noAccount', { year }) : '—'}
              </Typography>
            )}
          </CardContent>
        </Card>}

        <Card variant="outlined" sx={{ ...cardSx, flex: 1.15 }}>
          <CardContent sx={cardContentSx}>
            <CardHeading icon={<TrendingUpOutlinedIcon />} title={t('mobility.self.profileCardTitle')} subtitle={t('mobility.self.profileCardSubtitle')} />
            {category ? (
              <>
                <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
                  <CategoryTile label={t('mobility.self.assignedCategory')} value={category.currentCategory ?? t('mobility.self.noCategory')} primary />
                  {budgetEnabled && <CategoryTile label={t('mobility.self.vehicleCategory')} value={category.currentVehicleCategory ?? '—'} />}
                </Stack>

                {category.employeeType === 'Overhead' ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('mobility.self.overheadCategoryInfo')}
                  </Alert>
                ) : category.vehicleLeaseStartDate && category.vehicleLeaseEndDate && category.nextCategory && category.nextCategoryEffectiveDate ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('mobility.self.categoryRuleUpgrade', {
                      startDate: formatBelgianDate(category.vehicleLeaseStartDate),
                      endDate: formatBelgianDate(category.vehicleLeaseEndDate),
                      calculatedCategory: category.eligibleCategory,
                      assignedCategory: category.currentCategory ?? t('mobility.self.noCategory'),
                      nextCategory: category.nextCategory,
                      switchDate: formatBelgianDate(category.nextCategoryEffectiveDate),
                    })}
                  </Alert>
                ) : category.vehicleLeaseStartDate && category.vehicleLeaseEndDate ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('mobility.self.categoryRuleNoUpgrade', {
                      startDate: formatBelgianDate(category.vehicleLeaseStartDate),
                      endDate: formatBelgianDate(category.vehicleLeaseEndDate),
                      calculatedCategory: category.eligibleCategory,
                      assignedCategory: category.currentCategory ?? t('mobility.self.noCategory'),
                    })}
                  </Alert>
                ) : category.nextCategory && category.nextCategoryEffectiveDate ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('mobility.self.nextCategoryInfo', { category: category.nextCategory, date: formatBelgianDate(category.nextCategoryEffectiveDate) })}
                  </Alert>
                ) : null}

                {budgetEnabled && <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <AccountBalanceWalletOutlinedIcon color="primary" />
                    <Typography fontWeight={700}>{t('mobility.self.vehicleDetailsTitle')}</Typography>
                  </Stack>
                  {category.vehicleLeaseStartDate && category.vehicleLeaseEndDate ? (
                    <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                      <MoneyMetric label={t('mobility.self.entitledMonthlyTco')} helper={t('mobility.self.entitledMonthlyTcoHelper')} value={formatCurrency((category.annualEntitledTco ?? 0) / 12, category.vehicleLeaseCurrency, i18n.language)} />
                      <MoneyMetric label={t('mobility.self.vehicleMonthlyTco')} helper={t('mobility.self.vehicleMonthlyTcoHelper')} value={formatCurrency((category.annualVehicleTco ?? 0) / 12, category.vehicleLeaseCurrency, i18n.language)} />
                      <MoneyMetric label={t('mobility.self.monthlyMobilityDifference')} helper={t('mobility.self.monthlyMobilityDifferenceHelper')} value={formatCurrency(Math.max(0, ((category.annualEntitledTco ?? 0) - (category.annualVehicleTco ?? 0)) / 12), category.vehicleLeaseCurrency, i18n.language)} accent />
                    </Box>
                    </>
                  ) : <Typography variant="body2" color="text.secondary">{t('mobility.self.noVehicle')}</Typography>}
                </>}
              </>
            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
          </CardContent>
        </Card>
      </Stack>

      {budgetEnabled && <Box>
        <Typography variant="h6" fontWeight={750} sx={{ mb: 0.5 }}>{t('mobility.self.ledgerTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('mobility.self.ledgerSubtitle')}</Typography>
        <DataGrid
          autoHeight
          rows={visibleLedger}
          columns={columns}
          loading={ledgerLoading}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          localeText={{ noRowsLabel: t('mobility.self.noTransactions'), MuiTablePagination: { labelRowsPerPage: t('mobility.self.rowsPerPage') } }}
          sx={{ borderRadius: 2.5, bgcolor: 'background.paper', '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' } }}
        />
      </Box>}

      {switchingRelease && account && (
        <SwitchToYearlyReleaseDialog year={year} onClose={() => setSwitchingRelease(false)} />
      )}
    </Stack>
  );
}

/**
 * Confirmation dialog for switching a mobility account from monthly to
 * yearly (lump-sum) budget release. Requires an explicit acknowledgement
 * checkbox before the switch can be confirmed, since it's a one-way choice
 * for the year with leaver-forfeiture implications.
 *
 * @param year - The year the release mode change applies to.
 * @param onClose - Called to dismiss the dialog, whether confirmed or cancelled.
 * @returns The confirmation dialog.
 */
function SwitchToYearlyReleaseDialog({ year, onClose }: { year: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [acknowledged, setAcknowledged] = useState(false);
  const { mutate, isPending } = useSelectYearlyRelease();

  const handleConfirm = () => {
    mutate(year, {
      onSuccess: () => {
        enqueueSnackbar(t('mobility.self.switchToYearlyReleaseSuccess', { year }), { variant: 'success' });
        onClose();
      },
      onError: (error) => enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' }),
    });
  };

  return (
    <Dialog open onClose={isPending ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Typography variant="h5" fontWeight={750}>{t('mobility.self.switchToYearlyReleaseTitle')}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('mobility.self.switchToYearlyReleaseWarning', { year })}
        </Alert>
        <FormControlLabel
          control={<Checkbox checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />}
          label={t('mobility.self.switchToYearlyReleaseConfirmLabel')}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!acknowledged || isPending} onClick={handleConfirm}>
          {t('mobility.self.switchToYearlyReleaseConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const cardSx = {
  flex: 1,
  borderRadius: 3,
  boxShadow: '0 8px 28px rgba(15, 23, 42, 0.06)',
};

const cardContentSx = {
  p: { xs: 2.5, md: 3 },
  '&:last-child': { pb: { xs: 2.5, md: 3 } },
};

/**
 * Renders a card's icon, title, and subtitle heading block.
 *
 * @param icon - The icon to display in the leading badge.
 * @param title - The heading text.
 * @param subtitle - The supporting text below the heading.
 * @param primary - When true, styles the icon badge with the primary color instead of a neutral one.
 * @returns The card heading.
 */
function CardHeading({ icon, title, subtitle, primary = false }: { icon: ReactNode; title: string; subtitle: string; primary?: boolean }) {
  return <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'grid', placeItems: 'center', width: 44, height: 44, borderRadius: 2, bgcolor: primary ? 'primary.main' : 'action.hover', color: primary ? 'primary.contrastText' : 'primary.main' }}>{icon}</Box>
    <Box><Typography fontWeight={750}>{title}</Typography><Typography variant="body2" color="text.secondary">{subtitle}</Typography></Box>
  </Stack>;
}

/**
 * Renders a single label/value row within a {@link BudgetGroup}.
 *
 * @param label - The line's label.
 * @param value - The formatted value to display.
 * @param accent - When true, highlights the value in the primary color.
 * @returns The label/value row.
 */
function BudgetLine({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={700} color={accent ? 'primary.main' : 'text.primary'}>{value}</Typography>
  </Stack>;
}

/**
 * Renders a titled, boxed group of {@link BudgetLine} rows.
 *
 * @param title - The group's overline title.
 * @param children - The group's content, typically one or more `BudgetLine`s.
 * @returns The grouped box.
 */
function BudgetGroup({ title, children }: { title: string; children: ReactNode }) {
  return <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
    <Typography variant="overline" color="text.secondary" fontWeight={750}>{title}</Typography>
    <Stack spacing={1} sx={{ mt: 0.5 }}>{children}</Stack>
  </Paper>;
}

/**
 * Renders a single monthly money metric tile (label, amount, and helper text).
 *
 * @param label - The metric's label.
 * @param helper - Supporting explanatory text shown below the value.
 * @param value - The formatted monetary value to display.
 * @param accent - When true, highlights the tile with a primary-color border and value.
 * @returns The metric tile.
 */
function MoneyMetric({ label, helper, value, accent = false }: { label: string; helper: string; value: string; accent?: boolean }) {
  const { t } = useTranslation();
  return <Paper
    variant="outlined"
    sx={{ p: 1.5, borderRadius: 2, borderColor: accent ? 'primary.main' : 'divider', bgcolor: accent ? 'rgba(25, 118, 210, 0.06)' : 'transparent' }}
  >
    <Typography variant="caption" color="text.secondary" display="block" sx={{ minHeight: 36 }}>{label}</Typography>
    <Typography variant="h6" fontWeight={750} color={accent ? 'primary.main' : 'text.primary'}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{t('mobility.self.perMonth')}</Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75, lineHeight: 1.35 }}>{helper}</Typography>
  </Paper>;
}

/**
 * Renders a single category summary tile (label and value).
 *
 * @param label - The tile's label.
 * @param value - The value to display.
 * @param primary - When true, highlights the tile with a primary-color border and value.
 * @returns The category tile.
 */
function CategoryTile({ label, value, primary = false }: { label: string; value: string; primary?: boolean }) {
  return <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 1.5, borderRadius: 2, textAlign: 'center', borderColor: primary ? 'primary.main' : 'divider', bgcolor: primary ? 'rgba(25, 118, 210, 0.06)' : 'transparent' }}>
    <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
    <Typography variant="h5" fontWeight={750} color={primary ? 'primary.main' : 'text.primary'}>{value}</Typography>
  </Paper>;
}

/**
 * Builds a human-readable description for a ledger entry, enriching
 * expense-related entries with the linked claim's expense type when the
 * source claim is available in the current year's claims.
 *
 * @param entry - The ledger entry to describe.
 * @param claimsById - The current year's claims, keyed by claim id, used to look up `entry.sourceId`.
 * @param t - The i18next translation function.
 * @returns The localized description to display for this entry.
 */
function ledgerDescription(entry: MobilityLedgerEntry, claimsById: Map<number, ExpenseClaim>, t: TFunction) {
  if (entry.entryType === 'MonthlyRelease') return t('mobility.ledger.descriptions.MonthlyRelease');
  if (entry.entryType === 'YearlyRelease') return t('mobility.ledger.descriptions.YearlyRelease');
  const claim = entry.sourceId ? claimsById.get(entry.sourceId) : undefined;
  if (entry.entryType === 'ExpenseReimbursement' && claim) {
    return t('mobility.ledger.descriptions.ExpenseReimbursementDetailed', { type: claim.expenseTypeName });
  }
  if (entry.entryType === 'ExpenseReimbursement') return t('mobility.ledger.descriptions.ExpenseReimbursement', { id: entry.sourceId ?? '' });
  if (entry.entryType === 'Adjustment' && entry.sourceType === 'ExpenseClaim' && claim) {
    return t('mobility.ledger.descriptions.ExpenseRefundDetailed', { type: claim.expenseTypeName });
  }
  if (entry.entryType === 'Adjustment' && entry.sourceType === 'ExpenseClaim') return t('mobility.ledger.descriptions.ExpenseRefund', { id: entry.sourceId ?? '' });
  return entry.description;
}
